import React, { useState, useEffect } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Layers, Sparkles, BrainCircuit, Globe2,
  CalendarRange, Radio, Settings2, Menu, X, LogOut,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Footer from "@/components/Footer";

const NAV = [
  { label: "Performance HQ", short: "Home", icon: LayoutDashboard, path: "/dashboard", mobile: true },
  { label: "Campaign Organizer", short: "Campaigns", icon: Layers, path: "/campaigns", mobile: true },
  { label: "AI Ad Studio", short: "Studio", icon: Sparkles, path: "/studio", mobile: true },
  { label: "AI Performance Analyst", short: "Analyst", icon: BrainCircuit, path: "/analyst", mobile: true },
  { label: "Global Market Intel", short: "Markets", icon: Globe2, path: "/markets" },
  { label: "Reports & Calendar", short: "Reports", icon: CalendarRange, path: "/reports", mobile: true },
  { label: "Meta Integration", short: "Meta", icon: Radio, path: "/meta-integration", statusBadge: true },
  { label: "Workspace & Brand", short: "Brand", icon: Settings2, path: "/settings" },
];

function SidebarContent({ onNavigate, live }) {
  const { user, logout } = useAuth();
  return (
    <div className="flex flex-col h-full w-full min-w-0 bg-[#080A0B]">
      <div className="px-5 py-6 flex items-center gap-3 border-b border-[#242B30] min-w-0">
        {user?.logo_url ? (
          <img src={user.logo_url} alt="logo" className="h-10 w-10 rounded-xl object-cover" />
        ) : (
          <img src="/karyow-logo.png" alt="KARYOW" className="h-10 w-10 rounded-xl object-cover shrink-0" />
        )}
        <div className="min-w-0">
          <div className="font-display font-bold text-white leading-none truncate">
            {user?.brand_name || "KARYOW"}
          </div>
          <div className="font-mono text-[9px] tracking-[0.22em] text-lime-400 uppercase mt-1">
            AI Ads Manager
          </div>
        </div>
      </div>

      <nav className="flex-1 px-4 py-5 space-y-1.5 overflow-y-auto overflow-x-hidden">
        {NAV.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            data-testid={`nav-${item.path.slice(1)}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm min-w-0 transition-colors ${
                isActive
                  ? "bg-lime-400/10 text-lime-400 border border-lime-400/30"
                  : "text-zinc-400 hover:text-white hover:bg-[#14181B] border border-transparent"
              }`
            }
          >
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="truncate flex-1">{item.label}</span>
            {item.statusBadge && (
              <span className={`shrink-0 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${live
                ? "bg-lime-400/15 text-lime-400 border-lime-400/30"
                : "bg-amber-500/15 text-amber-400 border-amber-500/30"}`}>
                {live ? "Live" : "Off"}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-[#242B30]">
        <div className="flex items-center gap-3 px-2 py-2">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-[#242B30]" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white truncate">{user?.name}</div>
            <div className="text-[11px] text-zinc-500 truncate font-mono">{user?.email}</div>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="text-zinc-500 hover:text-rose-400 transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const location = useLocation();
  const current = NAV.find((n) => n.path === location.pathname);
  const live = !!(meta?.connected && meta?.ad_account_id);

  useEffect(() => {
    api.get("/meta/status").then((r) => setMeta(r.data)).catch(() => {});
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0A0D0E] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 xl:w-[292px] shrink-0 overflow-hidden border-r border-[#242B30] h-screen sticky top-0">
        <SidebarContent live={live} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-[300px] max-w-[85vw] overflow-hidden border-r border-[#242B30] h-full">
            <button
              className="absolute top-4 right-3 p-2 rounded-lg text-zinc-400 active:bg-[#1B2226] z-10"
              onClick={() => setMobileOpen(false)}
              data-testid="close-mobile-nav"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent live={live} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 bg-[#0F1315]/95 backdrop-blur-xl border-b border-[#242B30] px-4 sm:px-8 lg:px-10 py-3.5 sm:py-5 flex items-center gap-3 sm:gap-4">
          <button
            className="lg:hidden -ml-2 p-2 rounded-lg text-zinc-300 active:bg-[#1B2226] transition-colors"
            onClick={() => setMobileOpen(true)}
            data-testid="open-mobile-nav"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-base sm:text-xl font-semibold text-white truncate">
              {current?.label || "Performance HQ"}
            </h2>
          </div>
          <Link to="/meta-integration" data-testid="header-meta-status"
            className={`flex items-center gap-2 rounded-full border px-2.5 sm:px-3 py-2 transition-colors ${live
              ? "bg-lime-400/10 border-lime-400/30 hover:bg-lime-400/15"
              : "bg-[#14181B] border-amber-500/30 hover:border-amber-500/50"}`}>
            <span className={`h-2 w-2 rounded-full animate-pulse-dot ${live ? "bg-lime-400" : "bg-amber-400"}`} />
            <span className={`text-[11px] font-mono uppercase tracking-wider ${live ? "text-lime-400" : "text-amber-400"}`}>
              <span className="hidden sm:inline">{live ? "Live Meta Data" : "Meta Not Connected"}</span>
              <span className="sm:hidden">{live ? "Live" : "Off"}</span>
            </span>
          </Link>
        </header>
        <main className="flex-1 w-full px-4 sm:px-8 lg:px-10 py-5 sm:py-7 lg:py-9 pb-24 lg:pb-9 overflow-x-hidden">
          <div className="mx-auto w-full max-w-[1600px] min-w-0">
            {children}
            <Footer />
          </div>
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[#242B30] bg-[#080A0B]/95 backdrop-blur-xl"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="grid grid-cols-5">
          {NAV.filter((n) => n.mobile).map((item) => (
            <NavLink key={item.path} to={item.path}
              data-testid={`mobile-nav-${item.path.slice(1)}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] transition-colors ${
                  isActive ? "text-lime-400" : "text-zinc-500 active:text-white"
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              <span className="text-[10px] font-mono uppercase tracking-wider">{item.short}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
